import { useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { dailyTotals } from '@dairy/domain';
import { birrInputValue, litersInputValue, PAYMENT_METHODS, type BuyerRecord, type PaymentMethod, type PaymentRecord, type SaleRecord } from '@dairy/shared';
import { useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LitersInput, MoneyInput } from '@/components/inputs';
import { Button, Card, Field, Select } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { toast } from '@/components/toast';
import { db } from '@/db/db';
import { isLocked, saveMany, type Change } from '@/db/repo';
import { useFormat } from '@/lib/format';
import { useBuyerLedgers } from '@/features/money/useMoney';
import { calculateCustomerSale, customerDay, projectedCustomerBalance } from './dailyCustomerSales';

export function CustomerMilkSales({ date }: { date: string }) {
  const { t } = useTranslation();
  const profile = useProfile();
  const ledgers = useBuyerLedgers();
  const f = useFormat();
  const drafts = useRef(new Map<string, CustomerDraft>());
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const data = useLiveQuery(async () => {
    const [buyers, sales, payments, milk, usage] = await Promise.all([
      db.buyers.toArray(), db.sales.where('date').equals(date).toArray(), db.payments.toArray(),
      db.milkRecords.where('date').equals(date).toArray(), db.dailyMilkUsage.where('date').equals(date).toArray(),
    ]);
    return { buyers, sales, payments, saleable: dailyTotals(milk, usage, date).saleableMl };
  }, [date]);
  if (!data) return null;
  const buyers = data.buyers.filter((b) => b.active || data.sales.some((s) => s.type === 'MILK' && s.buyerId === b.id));
  const submit = async (force = false) => {
    const selected = [...drafts.current.values()].filter((draft) => draft.dirty && !draft.locked);
    const invalid = selected.find((draft) => !draft.result.ok);
    if (invalid) { setError(`${invalid.name}: ${t('milk.customerInvalid')}`); return; }
    const updatedIds = new Set(selected.map((draft) => draft.saleId));
    const otherSold = data.sales.filter((sale) => sale.type === 'MILK' && !sale.deletedAt && !updatedIds.has(sale.id)).reduce((sum, sale) => sum + (sale.milkMl ?? 0), 0);
    const added = selected.reduce((sum, draft) => sum + (draft.result.ok ? draft.result.ml : 0), 0);
    if (!force && otherSold + added > data.saleable) {
      setWarning(t('money.oversellWarning', { liters: f.liters(Math.max(0, data.saleable - otherSold)) }));
      return;
    }
    if (!selected.length) return;
    setSaving(true);
    try {
      await saveMany(selected.flatMap((draft) => draft.changes));
      selected.forEach((draft) => draft.markSaved());
      setError('');
      toast(navigator.onLine ? t('common.saved') : t('common.savedOffline'));
    } catch (e) { setError(errorMessage(t, e)); }
    finally { setSaving(false); }
  };
  return <div className="space-y-3">
    <h2 className="text-lg font-semibold">{t('milk.customerSales')}</h2>
    {buyers.length === 0 ? <p>{t('milk.noCustomers')} <Link className="underline" to="/buyers">{t('money.buyers')}</Link></p> : null}
    {buyers.map((buyer) => <CustomerRow key={`${date}:${buyer.id}`} buyer={buyer} date={date} data={data} tenantId={profile.tenantId} balance={ledgers?.find((entry) => entry.buyer.id === buyer.id)?.ledger.balanceSantim} drafts={drafts.current} />)}
    {error ? <p className="text-red-700" role="alert">{error}</p> : null}
    {buyers.length > 0 ? <Button className="w-full" onClick={() => void submit()} disabled={saving}>{t('common.save')}</Button> : null}
    <ConfirmDialog open={!!warning} onOpenChange={(open) => { if (!open) setWarning(null); }} title={t('common.areYouSure')} confirmLabel={t('common.saveAnyway')} onConfirm={() => submit(true)} confirmDisabled={saving}>
      <p>{warning}</p>
    </ConfirmDialog>
  </div>;
}

type SalesData = { buyers: BuyerRecord[]; sales: SaleRecord[]; payments: PaymentRecord[]; saleable: number };
type CustomerDraft = { name: string; dirty: boolean; locked: boolean; saleId: string; result: ReturnType<typeof calculateCustomerSale>; changes: Change[]; markSaved: () => void };

function CustomerRow({ buyer, date, data, tenantId, balance, drafts }: { buyer: BuyerRecord; date: string; tenantId: string; data: SalesData; balance?: number; drafts: Map<string, CustomerDraft> }) {
  const { t } = useTranslation();
  const f = useFormat();
  const profile = useProfile();
  const settings = useSettings();
  const day = customerDay(tenantId, date, buyer.id, data.sales, data.payments);
  const [liters, setLiters] = useState('');
  const [price, setPrice] = useState('');
  const [paid, setPaid] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setLiters(litersInputValue(day.managed?.milkMl));
    setPrice(birrInputValue(day.managed?.unitPriceSantim ?? buyer.pricePerLiterSantim));
    setPaid(birrInputValue(day.payment?.amountSantim));
    setMethod(day.payment?.method ?? 'CASH');
  }, [date, buyer.id, day.managed?.id, day.managed?.milkMl, day.managed?.unitPriceSantim, day.payment?.id, day.payment?.amountSantim, day.payment?.method, buyer.pricePerLiterSantim]);
  const saleLocked = !!day.managed && profile.role !== 'OWNER' && (day.managed.createdBy !== profile.userId || isLocked(day.managed, profile, settings));
  const paymentLocked = !!day.payment && profile.role !== 'OWNER' && (day.payment.createdBy !== profile.userId || isLocked(day.payment, profile, settings));
  const locked = saleLocked || paymentLocked || day.historical.length > 0;
  const result = calculateCustomerSale(liters, price, paid, day.externalPaid);
  const valid = result.ok && (result.ml > 0 || !day.managed);
  const projectedBalance = balance === undefined ? undefined : dirty && result.ok
    ? projectedCustomerBalance(balance, day.managed?.amountSantim ?? 0, day.payment?.amountSantim ?? 0, result.amountSantim, result.paidSantim)
    : balance;
  useEffect(() => {
    const changes: Change[] = [];
    if (result.ok && valid) {
      if (result.ml) changes.push({ entity: 'sale', op: 'upsert', record: { ...(day.managed ?? {}), id: day.saleId, type: 'MILK', date, buyerId: buyer.id, milkMl: result.ml, unitPriceSantim: result.priceSantim, amountSantim: result.amountSantim, animalId: null, description: null } });
      if (result.paidSantim) changes.push({ entity: 'payment', op: 'upsert', record: { ...(day.payment ?? {}), id: day.paymentId, buyerId: buyer.id, date, amountSantim: result.paidSantim, method, reference: null, saleId: result.ml ? day.saleId : null } });
      else if (day.payment) changes.push({ entity: 'payment', op: 'delete', id: day.paymentId });
    }
    const hasEntry = !!day.managed || liters.trim() !== '' || paid.trim() !== '';
    drafts.set(buyer.id, { name: buyer.name, dirty: dirty && hasEntry, locked, saleId: day.saleId, result: valid ? result : { ok: false }, changes, markSaved: () => setDirty(false) });
    return () => { drafts.delete(buyer.id); };
  });
  return <Card className="space-y-3">
    <h3 className="font-semibold">{buyer.name}</h3>
    <p>{t('money.balance')}: {balance === undefined ? '—' : <span className={balance < 0 ? 'text-green-800' : balance > 0 ? 'text-red-800' : ''}>{balance < 0 ? t('money.credit') : balance > 0 ? t('money.owes') : ''} {f.birr(Math.abs(balance))}</span>}</p>
    {day.historical.length ? <div><p>{t('milk.historicalSales')}</p>{day.historical.map((sale) => <p key={sale.id}><Link className="underline" to={`/sales/${sale.id}/edit`}>{f.liters(sale.milkMl)} · {f.birr(sale.amountSantim)}</Link></p>)}</div> : <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('milk.customerLiters')}><LitersInput value={liters} onChange={(e) => { setLiters(e.target.value); setDirty(true); }} disabled={locked} /></Field>
        <Field label={t('milk.customerPrice')}><MoneyInput value={price} onChange={(e) => { setPrice(e.target.value); setDirty(true); }} disabled={locked || profile.role !== 'OWNER'} /></Field>
        <Field label={t('milk.customerPaid')}><MoneyInput value={paid} onChange={(e) => { setPaid(e.target.value); setDirty(true); }} disabled={locked} /></Field>
        <Field label={t('milk.customerMethod')}><Select value={method} onChange={(e) => { setMethod(e.target.value as PaymentMethod); setDirty(true); }} disabled={locked}>{PAYMENT_METHODS.map((m) => <option key={m} value={m}>{t(`money.methodType.${m}`)}</option>)}</Select></Field>
      </div>
      <p>{t('milk.customerAmount')}: {f.birr(result.ok ? result.amountSantim : 0)}</p>
      <p>{t('milk.otherPayments')}: {f.birr(day.externalPaid)}</p>
      <p>{t('milk.balanceAfterSale')}: {projectedBalance === undefined || (dirty && !valid && (liters.trim() !== '' || paid.trim() !== '')) ? '—' : <span className={projectedBalance < 0 ? 'text-green-800' : projectedBalance > 0 ? 'text-red-800' : ''}>{projectedBalance < 0 ? t('money.credit') : projectedBalance > 0 ? t('money.owes') : ''} {f.birr(Math.abs(projectedBalance))}</span>}</p>
      {locked ? <p>{t('common.lockedAskOwner')}</p> : null}
    </>}
  </Card>;
}

